import { Box, Button, Grid, MenuItem, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import dayjs from "dayjs";
import getAxiosError from "../../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import Loader from "../../../../components/common/loader/Loader";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import { AddRtsRequest } from "../../../../app/models/requestModels/RtsRequestAdminPanel";
import { persistNewRts } from "../../../../app/features/dedicatedDelivery/rtsSlice";
import { FdpAgent } from "../../../../app/api/fdp/FdpAgent";
import { UserDataResponse } from "../../../../app/models/responseModels/userDataUiResponse";
import { StoreResponse } from "../../../../app/models/responseModels/storeResponse";
import { format, utcToZonedTime } from "date-fns-tz";
import { AddRtsAgent } from "../../../../app/api/rts/AddRtsAgent";
import { RtsEventTypesResponse } from "../../../../app/models/responseModels/rtsEventTypesResponse";
import FormikDatePicker from "../../../../components/common/formikFields/FormikDatePicker";

const today = dayjs();
const dateBeforeThreeMonths = dayjs().subtract(3, "month");
interface AddRtsModalProps {
  clientIdArg: number;
  clientNameArg: string;
  closeAddRtsModal: CallableFunction;
  onCompleted: CallableFunction;
}
function AddRtsFormModel(
  userId: string,
  storeId: number,
  eventTypeId: number,
  operationDate: string,
  latitude: string,
  longitude: string,
  scanTimeInGmt: string,
  scanLocalTime: string,
  scanType: string,
  action: string,
  otherReason?: string,
  barcode?: string,
  imageUrl?: string
): AddRtsRequest {
  return {
    userId: userId,
    storeId: storeId,
    eventTypeId: eventTypeId,
    otherReason: otherReason,
    operationDate: operationDate,
    latitude: latitude,
    longitude: longitude,
    scanTimeInGmt: scanTimeInGmt,
    scanLocalTime: scanLocalTime,
    scanType: scanType,
    action: action,
    barcode: barcode,
    imageUrl: imageUrl,
  };
}

const AddRtsModal = ({
  clientIdArg,
  clientNameArg,
  closeAddRtsModal,
  onCompleted,
}: AddRtsModalProps) => {
  const initialValues: AddRtsRequest = {
    userId: "",
    storeId: 0,
    eventTypeId: 0,
    otherReason: "",
    operationDate: today.toDate(),
    latitude: "",
    longitude: "",
    scanTimeInGmt: "",
    scanLocalTime: "",
    scanType: "",
    barcode: "",
    action: "",
  };
  const dispatch = useAppDispatch();
  const { isFdpLoading, fdpListByClient } = useSelector(
    (state: RootState) => state.fdp
  );
  const [errFlag, setErrFlag] = useState<boolean>(false);
  const { rtsEventTypes } = useSelector((state: RootState) => state.rts);
  const [serviceLoading, setServiceLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [driverData, setDriverData] = useState<UserDataResponse[]>();
  const [storeData, setStoreData] = useState<StoreResponse[]>();
  const [reasonTypes, setReasonTypes] = useState<RtsEventTypesResponse[]>([]);

  useEffect(() => {
    if (rtsEventTypes) {
      setReasonTypes([
        ...rtsEventTypes,
        {
          id: -1,
          eventTypeId: -1,
          eventName: "Other",
          eventDescription: "Other",
        },
      ]);
    }
  }, [rtsEventTypes]);

  const validationSchema = Yup.object().shape({
    userId: Yup.string()
      .required("Driver is required")
      .test(
        "driver-is-not-empty",
        "Driver cannot be blank. ",
        (value) => value !== ""
      ),
    storeId: Yup.number()
      .required("Store is required")
      .test(
        "store-is-not-empty",
        "Store cannot be blank. ",
        (value) => value !== 0
      ),
    eventTypeId: Yup.number()
      .required("Reason is required")
      .test(
        "event-is-not-empty",
        "Reason cannot be blank. ",
        (value) => value !== 0
      ),
    otherReason: Yup.string().test(
      "otherReason-required-if-eventTypeId-is-minus-1",
      "Please specify the reason.",
      function (value: any) {
        // `this.parent` refers to the parent object (the entire form values)
        return this.parent.eventTypeId !== -1 || (value && value.trim() !== "");
      }
    ),
    barcode: Yup.string().test(
      "barcode-max-min-validation",
      "Barcode length can't be less than 3 and greater than 30.",
      (value) => {
        if (value && value !== "") {
          const trimmedValueLength = value.trim().length;
          return trimmedValueLength >= 3 && trimmedValueLength <= 30;
        }
        return true;
      }
    ),
    fileInput: Yup.mixed().test(
      "required",
      "Please select a file",
      (files) => !files
    ),
  });

  // ***** Handlers *****
  const handleSubmit = async (
    values: AddRtsRequest,
    { setFieldError, resetForm }: any
  ) => {
    var imageUrl: any = undefined;
    if (
      values.storeId &&
      values.userId &&
      values.eventTypeId &&
      !values.barcode &&
      !selectedFile
    ) {
      setFieldError(
        "fileInput",
        "Either barcode or image selection one of them is mandatory."
      );
      setErrFlag(true);
      return false;
    }
    setErrFlag(false);
    if (selectedFile) {
      var files: string[] = [selectedFile];
      await AddRtsAgent.uploadImage({
        files: files,
      }).then(
        (resp) => {
          imageUrl = resp?.imageStatus[0]?.accessUrl;
          submitData(values, imageUrl);
        },
        (err) => {
          toast.error("Error while uploading the image.");
        }
      );
    } else {
      submitData(values);
    }
    resetForm();
    return true;
  };

  async function submitData(values: AddRtsRequest, imageUrl?: string) {
    try {
      const addRtsDetails = AddRtsFormModel(
        values.userId,
        values.storeId,
        values.eventTypeId,
        dayjs(values.operationDate).format("YYYY-MM-DD"),
        storeData
          ? storeData.filter((store) => store.id === values.storeId)[0].latitude
          : "",
        storeData
          ? storeData.filter((store) => store.id === values.storeId)[0]
              .longitude
          : "",
        format(utcToZonedTime(new Date(), "UTC"), "yyyy-MM-dd'T'HH:mm:ss") +
          "Z",
        format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
        "system",
        "open",
        values.eventTypeId === -1 ? values.otherReason?.trim() : undefined,
        values?.barcode,
        imageUrl
      );
      const response = await dispatch(persistNewRts(addRtsDetails)).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "rtsData",
        });
      } else {
        toast.success("RTS has been added successfully.");
        closeAddRtsModal();
        onCompleted();
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "addRtsException",
      });
    }
  }

  async function fetchDriversByFdp(fdpId: number) {
    try {
      const response = await FdpAgent.getDriversByFdp(fdpId);
      return response.data;
    } catch (error) {
      // Handle errors here, e.g., log them or throw a custom error
      console.error("Error fetching drivers by FDP:", error);
      throw error; // Rethrow the error to be handled by the caller if necessary
    }
  }

  async function fetchStoresByFdp(fdpId: number) {
    try {
      const response = await FdpAgent.getStoresByFdpId(fdpId, clientIdArg);
      return response.data;
    } catch (error) {
      // Handle errors here, e.g., log them or throw a custom error
      console.error("Error fetching stores by FDP:", error);
      throw error; // Rethrow the error to be handled by the caller if necessary
    }
  }

  // Function to convert a file to base64 format
  function fileToBase64(
    file: File,
    callback: (base64String: string) => void
  ): void {
    const reader = new FileReader();
    reader.onload = function (event) {
      const result = event.target?.result as string;
      callback(result);
    };
    reader.readAsDataURL(file);
  }

  return (
    <>
      <Loader isLoading={isFdpLoading || serviceLoading} />
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(
          values: AddRtsRequest,
          { setSubmitting, setFieldError, resetForm }
        ) => {
          setSubmitting(true);
          handleSubmit(values, { setFieldError, resetForm });
          setSubmitting(false);
        }}
      >
        {({ values }) => (
          <SubmitModalWithFormik
            title={"Add New " + clientNameArg + " RTS"}
            closeSubmitModal={closeAddRtsModal}
            childErrorMessage={""}
            allowSubmit={false}
            size="lg"
            isLoading={false}
            submitButtonName="Add"
            closeModalAfterSubmit={false}
            key="submitModal"
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Form>
                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <FormikTextField
                      margin="normal"
                      id="fdpId"
                      name="fdpId"
                      label="FDP"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                      onChange={(e: any) => {
                        setDriverData(undefined);
                        setStoreData(undefined);
                        setServiceLoading(true);
                        fetchDriversByFdp(e.target.value).then((drivers) => {
                          setDriverData(drivers);
                        });
                        fetchStoresByFdp(e.target.value).then((stores) => {
                          setStoreData(stores);
                        });
                        setServiceLoading(false);
                      }}
                    >
                      {fdpListByClient ? (
                        fdpListByClient.map((fdp, index) => (
                          <MenuItem key={index} value={fdp.id}>
                            {fdp.companyName}
                          </MenuItem>
                        ))
                      ) : (
                        <div></div>
                      )}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="userId"
                      name="userId"
                      label="Driver"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {driverData ? (
                        driverData.map((driver, index) => (
                          <MenuItem
                            key={index}
                            value={driver.id}
                            disabled={driver.deleted}
                          >
                            {driver.firstName} {driver.lastName}
                          </MenuItem>
                        ))
                      ) : (
                        <div></div>
                      )}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="storeId"
                      name="storeId"
                      label="Store"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {storeData &&
                        storeData.map((store, index) => (
                          <MenuItem key={index} value={store.id}>
                            {store.storeId}
                          </MenuItem>
                        ))}
                    </FormikTextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="eventTypeId"
                      name="eventTypeId"
                      label="Reason"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                      required
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: { style: { maxHeight: 150 } },
                        },
                      }}
                    >
                      {reasonTypes &&
                        reasonTypes.map((eventType, index) => (
                          <MenuItem key={index} value={eventType.id}>
                            {eventType.eventDescription}
                          </MenuItem>
                        ))}
                    </FormikTextField>
                  </Grid>
                  {values.eventTypeId === -1 && (
                    <Grid item xs={12} sm={6} md={6} lg={6}>
                      <FormikTextField
                        margin="normal"
                        id="otherReason"
                        name="otherReason"
                        label="Other Reason"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required={values.eventTypeId === -1}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: { style: { maxHeight: 150 } },
                          },
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikDatePicker
                      name="operationDate"
                      label="Operation Date"
                      minDate={dateBeforeThreeMonths}
                      maxDate={today}
                      size="small"
                      margin="normal"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      margin="normal"
                      id="barcode"
                      name="barcode"
                      label="Barcode"
                      size="small"
                      fullWidth
                      variant={"outlined"}
                    />
                  </Grid>
                  <Grid container item xs={12} sm={6} md={6} lg={6}>
                    <FormikTextField
                      type="file"
                      style={{ display: "none" }}
                      id="fileInput"
                      onChange={(event: { target: HTMLInputElement }) => {
                        if (event.target.files !== (undefined || null)) {
                          const selectedFile = (
                            event.target as HTMLInputElement
                          ).files?.[0];
                          if (selectedFile) {
                            if (!selectedFile.type.startsWith("image/")) {
                              event.target.files = null;
                              return;
                            }
                            setSelectedFileName(selectedFile.name);
                            fileToBase64(selectedFile, function (base64String) {
                              setSelectedFile(base64String);
                            });
                          }
                        }
                      }}
                      name="fileInput"
                      variant="outlined"
                      label={"Select Image File"}
                    />
                    <label htmlFor="fileInput">
                      <Button variant="outlined" component="span">
                        Select Image File
                      </Button>
                    </label>
                    {selectedFileName && <span>{selectedFileName}</span>}
                  </Grid>
                </Grid>
                {errFlag && (
                  <Typography color="red" component="h3">
                    Either Barcode or File selection one of them is mandatory
                  </Typography>
                )}
              </Form>
            </Box>
          </SubmitModalWithFormik>
        )}
      </Formik>
    </>
  );
};

export default AddRtsModal;
